import { matrixToPermissionStrings } from '@evi/shared';
import { CHARGER_WRITE_PERMISSION, canEditPrivateAccess, type PrivateEditor } from './privateAccess';

/**
 * The Admin's pricing controls (Warren, 2026-10-25: "we must be able to set
 * our own prices in Admin"): a location's default tariff and a charger's own
 * tariff, read and written through GET/PATCH /locations/:id/tariff and
 * /chargers/:id/tariff. Pure, so the rules and the copy have tests.
 *
 *   who may edit   the permission string each PATCH checks, derived from the
 *                  shared resource map so it cannot drift from the server:
 *                  Location:update (location:write) for a location, the
 *                  Charger:update string PUT /chargers/:id checks for a
 *                  charger. Never a Site (both routes refuse one). The GET's
 *                  own can_edit must agree too, so a caller the server would
 *                  refuse (a scoped grant the client cannot see) is shown the
 *                  setting read-only.
 *   the choice     '' means "no tariff of its own": the location default for
 *                  a charger, the operator account's default for a location.
 */

export const LOCATION_WRITE_PERMISSION: string = matrixToPermissionStrings([{
  resource: 'Location', create: false, read: false, update: true, delete: false,
}] as never)[0] ?? 'location:write';

export { CHARGER_WRITE_PERMISSION };

export function canEditLocationTariff(user: PrivateEditor | null | undefined, isSite: boolean, serverCanEdit: boolean): boolean {
  if (!user || isSite || !serverCanEdit) return false;
  return user.role === 'super_admin' || (user.permissions ?? []).includes(LOCATION_WRITE_PERMISSION);
}

export function canEditChargerTariff(user: PrivateEditor | null | undefined, isSite: boolean, serverCanEdit: boolean): boolean {
  return serverCanEdit && canEditPrivateAccess(user, isSite);
}

export type TariffSource = 'charger' | 'location' | 'tenant' | 'fallback';
export type TariffRefState = 'none' | 'ok' | 'inactive' | 'missing';

export interface TariffSummary {
  _id: string;
  name: string;
  currency: 'USD' | 'CAD' | null;
  type: string;
  rate_type: 'per_kwh' | 'per_hour';
  energy_rate: number;
  rate_range: { min: number; max: number };
  tax_rate: number;
  idle_rate_cents: number | null;
  is_active: boolean;
  is_default: boolean;
}

export interface TariffRef {
  tariff_id: string | null;
  state: TariffRefState;
  tariff: TariffSummary | null;
}

interface ViewBase {
  location: { _id: string | null; name: string; type: string | null; currency: 'USD' | 'CAD' | null; currency_reason: string | null };
  effective: { source: TariffSource; source_label: string; tariff: TariffSummary | null };
  warnings: string[];
  can_edit: boolean;
  candidates: TariffSummary[];
  other_currency_count: number;
}

export interface LocationTariffView extends ViewBase {
  kind: 'location';
  default_tariff: TariffRef;
  tenant_default: TariffSummary | null;
  chargers: { total: number; own_tariff: number; own_tariff_skipped: number; using_default: number };
  connection_fees: Array<{ cents: number | null; chargers: number }>;
}

export interface ChargerTariffView extends ViewBase {
  kind: 'charger';
  charger: { _id: string; identity: string };
  own_tariff: TariffRef;
  location_default: TariffRef;
  connection_fee: { charger_cents: number | null; applies_cents: number };
}

const TYPE_LABELS: Record<string, string> = { flat: 'Flat', time_of_use: 'Time of use', tiered: 'Tiered' };

/** A rate in cents, as the Tariffs page shows it: 45 is "45.00¢/kWh". */
export function formatRate(cents: number, rateType: 'per_kwh' | 'per_hour'): string {
  return `${cents.toFixed(2)}¢/${rateType === 'per_hour' ? 'hour' : 'kWh'}`;
}

export function formatTax(rate: number): string {
  return `${(Math.round(rate * 10000) / 100).toFixed(2)}%`;
}

export function formatMoneyCents(cents: number, currency: 'USD' | 'CAD' | null): string {
  return `${currency === 'CAD' ? 'CA$' : 'US$'}${(cents / 100).toFixed(2)}`;
}

/** "45.00¢/kWh", or the span of a time-of-use or tiered tariff. */
export function rateText(t: TariffSummary): string {
  if (t.rate_range.min === t.rate_range.max) return formatRate(t.energy_rate, t.rate_type);
  return `${formatRate(t.rate_range.min, t.rate_type)} to ${formatRate(t.rate_range.max, t.rate_type)}`;
}

/** One line for a select option: name, currency, energy rate, tax. */
export function tariffOptionLabel(t: TariffSummary): string {
  return [t.name, t.currency ?? 'USD', rateText(t), `tax ${formatTax(t.tax_rate)}`].join(' · ');
}

/** The rows the effective-tariff panel shows. */
export function tariffDetailRows(t: TariffSummary, connectionFee: string): Array<{ label: string; value: string }> {
  return [
    { label: 'Currency', value: t.currency ?? 'USD' },
    { label: 'Type', value: TYPE_LABELS[t.type] ?? t.type },
    { label: 'Energy rate', value: rateText(t) },
    { label: 'Connection fee', value: connectionFee },
    { label: 'Tax', value: formatTax(t.tax_rate) },
    { label: 'Idle fee', value: t.idle_rate_cents ? `${formatMoneyCents(t.idle_rate_cents, t.currency)}/min` : 'None' },
  ];
}

/** The connection fee line for a charger: EVI's, per charger, and only on a
 *  session the tariff bills (a free charger stays free). */
export function chargerConnectionFeeText(v: ChargerTariffView): string {
  const currency = v.effective.tariff?.currency ?? v.location.currency;
  if (v.connection_fee.applies_cents > 0) return `${formatMoneyCents(v.connection_fee.applies_cents, currency)} a session (EVI, set on this charger)`;
  if ((v.connection_fee.charger_cents ?? 0) > 0) return 'None: this tariff bills nothing, so no connection fee applies';
  return 'None set on this charger';
}

/** The location's connection fees, which live on each charger. */
export function locationConnectionFeeText(v: LocationTariffView): string {
  if (v.connection_fees.length === 0) return 'Set on each charger';
  const parts = v.connection_fees.map((f) => {
    const amount = f.cents && f.cents > 0 ? formatMoneyCents(f.cents, v.location.currency) : 'none';
    return `${amount} on ${f.chargers} charger${f.chargers === 1 ? '' : 's'}`;
  });
  return `${parts.join(', ')} (set on each charger, EVI)`;
}

/** Where the tariff in use comes from, for the panel heading. */
export function effectiveHeading(v: LocationTariffView | ChargerTariffView): string {
  if (v.effective.source === 'fallback' || !v.effective.tariff) return 'No tariff: the fallback rate bills';
  const who = v.kind === 'location' ? 'Chargers here without their own tariff bill' : 'This charger bills';
  return `${who} ${v.effective.tariff.name}, from ${v.effective.source_label}`;
}

/** The stored choice as the select's value. */
export function storedChoice(v: LocationTariffView | ChargerTariffView): string {
  return (v.kind === 'location' ? v.default_tariff.tariff_id : v.own_tariff.tariff_id) ?? '';
}

/** The "no tariff of its own" option's label. */
export function noneChoiceLabel(v: LocationTariffView | ChargerTariffView): string {
  if (v.kind === 'charger') {
    const def = v.location_default.state === 'ok' && v.location_default.tariff ? v.location_default.tariff.name : null;
    return def ? `Use the location default (${def})` : 'Use the location default (none set)';
  }
  return v.tenant_default ? `No default: use the account default (${v.tenant_default.name})` : 'No default (the fallback rate bills)';
}

/** The PATCH body for a choice, with what the editor loaded. */
export function choiceBody(v: LocationTariffView | ChargerTariffView, choice: string):
  { default_tariff_id: string | null; expected_default_tariff_id: string | null } | { tariff_id: string | null; expected_tariff_id: string | null } {
  const next = choice === '' ? null : choice;
  const expected = storedChoice(v) || null;
  return v.kind === 'location'
    ? { default_tariff_id: next, expected_default_tariff_id: expected }
    : { tariff_id: next, expected_tariff_id: expected };
}

/** A stored choice that is no longer offered (inactive, gone, or another
 *  currency) still shows in the select, flagged, so the page never claims a
 *  setting it does not have. */
export function staleChoiceLabel(ref: TariffRef): string | null {
  if (!ref.tariff_id) return null;
  if (ref.state === 'inactive') return `${ref.tariff?.name ?? 'Unnamed tariff'} (inactive: billing skips it)`;
  if (ref.state === 'missing') return 'A tariff that no longer exists (billing skips it)';
  return null;
}
